import React from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { UserContainer } from '@/providers';
import { useSession } from '@/states/common';

interface PlanCardProps {
  type?: 'free' | 'starter' | 'professional' | 'team';
  dataSource?: string;
  workspace?: string;
  user?: string;
  onUpgrade?: () => void;
  showUpgradeButton?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  type = 'free',
  dataSource = '0/3',
  workspace = '0/1',
  user = '0/1',
  onUpgrade,
  showUpgradeButton = true,
}) => {
  const { t } = useTranslation('billing');
  const { state: userStateFromContainer } = UserContainer.useContainer();

  const { data: session } = useSession();
  const { billing, permission } = session;

  const formatNumber = (value: string): string => {
    const [current, total] = value.split('/');

    // If total is infinity symbol, return as-is
    if (total === '∞') {
      return `${current}/${total}`;
    }

    const currentNum = parseInt(current, 10);

    const formattedCurrent = currentNum === 0 ? '0' : current.padStart(2, '0');

    const formattedTotal = total.padStart(2, '0');

    return `${formattedCurrent}/${formattedTotal}`;
  };

  const calculateProgress = (current: number, total: number): number => {
    // If total is 0 or NaN (from infinity), return 0 progress
    if (!total || Number.isNaN(total)) return 0;
    return (current / total) * 100;
  };

  const calculateProgressColor = (current: number, total: number): string => {
    const progress = calculateProgress(current, total);
    if (progress >= 75) return 'bg-red-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const parseUsageValues = (value: string) => {
    const [current, total] = value.split('/');
    const currentNum = parseInt(current, 10);
    // If total is infinity symbol, return a very large number or 0 for progress calculation
    const totalNum = total === '∞' ? 0 : parseInt(total, 10);
    return { current: currentNum, total: totalNum };
  };

  const planConfig = {
    free: {
      title: 'Free',
      iconClass: 'ti ti-heart',
      buttonText: 'Upgrade Plan',
      buttonVariant: 'contain',
    },
    starter: {
      title: 'Starter',
      iconClass: 'ti ti-rocket',
      buttonText: 'Upgrade Plan',
      buttonVariant: 'contain',
    },
    professional: {
      title: 'Professional',
      iconClass: 'ti ti-briefcase',
      buttonText: 'Upgrade Plan',
      buttonVariant: 'contain',
    },
    team: {
      title: 'Team',
      iconClass: 'ti ti-buildings',
      buttonText: 'Change Plan',
      buttonVariant: 'outline',
    },
  };

  const normalizedType = type.toLowerCase() as keyof typeof planConfig;
  const config = planConfig[normalizedType] || planConfig.free;
  const userRole = permission?.role ?? 'member';

  if (userRole === 'member') {
    return (
      <div className="relative w-full max-w-xs flex flex-col py-4 px-5 rounded-lg border border-gray-200 bg-gradient-to-r from-white to-blue-50 text-sm overflow-hidden">
        <h3 className="text-xs font-semibold text-gray-600 text-center flex items-center gap-2">
          <i className={`${config.iconClass} text-primary-400 text-3xl`} />
          You are on {config.title} plan
        </h3>
      </div>
    );
  }

  return (
    <div className="relative w-full max-w-xs flex flex-col py-4 px-5 rounded-lg border border-gray-200 bg-gradient-to-r from-white to-blue-50 text-sm overflow-hidden">
      <div className="flex items-center justify-between mb-7">
        <h3 className="text-base font-semibold text-gray-900">
          {config.title}
        </h3>
        <div className="absolute -top-4 -right-4 w-16 h-16 flex items-center justify-center rounded-full bg-white shadow-[0_0_20px] shadow-primary-200">
          <i className={`${config.iconClass} text-primary-400 text-3xl`} />
        </div>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-y-2 gap-x-1 mb-4">
        {/* Data Source - Row 1 */}
        <div className="text-gray-600 text-xs pr-6">
          <span>{t('NewUserBillingPlan_DataSource', 'Data Source')}</span>
        </div>
        <Progress
          value={calculateProgress(
            parseUsageValues(dataSource).current,
            parseUsageValues(dataSource).total
          )}
          className="h-2 bg-gray-200 mr-2"
          indicatorColor={calculateProgressColor(
            parseUsageValues(dataSource).current,
            parseUsageValues(dataSource).total
          )}
        />
        <span className="text-xs text-nowrap text-right">
          {formatNumber(dataSource)}
        </span>

        {/* Workspace - Row 2 */}
        <div className="text-gray-600 text-xs pr-6">
          <span>{t('NewUserBillingPlan_Workspace', 'Workspace')}</span>
        </div>
        <Progress
          value={calculateProgress(
            parseUsageValues(workspace).current,
            parseUsageValues(workspace).total
          )}
          className="h-2 bg-gray-200 mr-2"
          indicatorColor={calculateProgressColor(
            parseUsageValues(workspace).current,
            parseUsageValues(workspace).total
          )}
        />
        <span className="text-xs text-nowrap text-right">
          {formatNumber(workspace)}
        </span>

        {/* User - Row 3 */}
        <div className="text-gray-600 text-xs pr-6">
          <span>{t('NewUserBillingPlan_User', 'User')}</span>
        </div>
        <Progress
          value={calculateProgress(
            parseUsageValues(user).current,
            parseUsageValues(user).total
          )}
          className="h-2 bg-gray-200 mr-2"
          indicatorColor={calculateProgressColor(
            parseUsageValues(user).current,
            parseUsageValues(user).total
          )}
        />
        <span className="text-xs text-nowrap text-right">
          {formatNumber(user)}
        </span>
      </div>

      {showUpgradeButton && (
        <Button
          variant={config.buttonVariant}
          className={cn(
            'w-full justify-center gap-2 py-2 px-4 text-xs font-medium',
            config.title === 'Team' && ' bg-white hover:bg-white'
          )}
          onClick={onUpgrade}
        >
          {config.title !== 'Team' && <i className="ti ti-bolt text-base" />}
          {config.buttonText}
        </Button>
      )}
    </div>
  );
};

export default PlanCard;
